import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckboxWithLabel, SimpleDialog } from "../components";
import { sendCustomEvent } from "../events";
import { useDialog } from "../hooks";
import {
  Projection,
  selectDataProjection,
  updateDataProjection,
  updateViewProjection
} from "../redux/projections-slice";
import { useTranslation } from "react-i18next";
import { Grid, Typography, useTheme } from "@mui/material";
import { resetAerialState, resetCameraState } from "../redux/camera-slice";
import { userProjections } from ".";

export const ProjectionDetector = () => {
  const dialog = useDialog();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [filename, setFilename] = useState<string>(null);
  const [projection, setProjection] = useState<Projection>(null);
  const [saveProjection, setSaveProjection] = useState(false);

  const dataProjection = useSelector(selectDataProjection);

  /** LAS projection event listener */
  useEffect(() => {
    const callback = (event) => {
      if (!dataProjection.default) return;

      const {filename, projection} = event.detail;

      setFilename(filename);
      setProjection(projection);
      dialog.handleOpen();
    };

    document.addEventListener("las-projection", callback);

    return () => {
      document.removeEventListener("las-projection", callback);
    };
  }, [dataProjection]);

  const onSubmit = () => {
    dispatch(resetCameraState(null));
    dispatch(resetAerialState(null));

    if (existingProjection) {
      dispatch(updateDataProjection(existingProjection));
      dispatch(updateViewProjection(existingProjection));
    } else {
      if (saveProjection) {
        userProjections.save(uniqueProjection);
      }

      dispatch(updateDataProjection(uniqueProjection));
      dispatch(updateViewProjection(uniqueProjection));
    }

    dialog.handleClose();
  };

  const existingProjection = projection
    ? userProjections.getByString(projection.string)
    : null;

  const uniqueProjection = projection
    ? userProjections.withUniqueName(projection)
    : null;

  const useExisting = !!existingProjection;
  const createNew = !existingProjection;

  const buttonText = existingProjection
    ? t('projections.use_projection')
    : t('projections.import_projection');

  return (
    <SimpleDialog
      open={dialog.open}
      onClose={dialog.handleClose}
      onSubmit={onSubmit}
      title={t('projections.projection_detected')}
      button={buttonText}
    >
      {useExisting && <Typography>
        {t('projections.las_projection_detected_existing')}
      </Typography>}

      {createNew && <Typography>
        {t('projections.las_projection_detected')}
      </Typography>}

      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
        paddingTop: theme.spacing(1.5)
      }}>
        <Typography variant="caption">
          <b>{t('projections.filename')}: </b> {filename}
        </Typography>

        {/** Information of existing projection */}
        {useExisting && existingProjection && <React.Fragment>
          <Typography variant="caption">
            <b>{t('projections.projection-name')}: </b> {existingProjection.name}
          </Typography>

          <Typography variant="caption">
            <b>{t('projections.projection_string')}: </b> {existingProjection.string}
          </Typography>
        </React.Fragment>}

        {/** Information for new projection (with unique name) */}
        {createNew && uniqueProjection && <React.Fragment>
          <Typography variant="caption">
            <b>{t('projections.projection-name')}: </b> {uniqueProjection.name}
          </Typography>

          <Typography variant="caption">
            <b>{t('projections.projection_string')}: </b> {uniqueProjection.string}
          </Typography>

          <Grid item xs={12} sx={{pt: 3}}>
            <CheckboxWithLabel
              title={t('projections.save_projection')}
              label={t('projections.the_new_projection_will_be_permanently_saved')}
              value={saveProjection}
              onChange={setSaveProjection}
            />
          </Grid>

        </React.Fragment>}
      </div>

    </SimpleDialog>
  );
};

export const setProjectionFromLAS = (filename: string, projection: Projection) => {
  sendCustomEvent("las-projection", {filename, projection});
};
